import { DEFAULT_LOGGER, Log } from "./logger";
import { METRICS } from "./telemetry/constants";
import { isESCPartition } from "./utils";

export const DEFAULT_GEOLOCATION_URL = "https://prod.tools.shortbread.aws.dev/1x1.png";
export const DEFAULT_CONSOLE_INTEGRATION_GEOLOCATION_URL = "https://prod.tools.shortbread.panorama.console.api.aws/ping";
export const FALLBACK_CONSOLE_INTEGRATION_GEOLOCATION_URL = "https://prod.tools.shortbread.analytics.console.aws.a2z.com/ping";
export const QUERY_PARAM_KEY = "awsccc";


export const timestampUrl = (url: string): string => {
    if (url.indexOf("?") !== -1) {
        const splittedUrl = url.split("?");
        // if url has params or if it has params AND hash values, insert our k-v pair immediately after the '?'
        url = splittedUrl[0] + `?${QUERY_PARAM_KEY}=${Date.now()}&` + splittedUrl[1]
    } else if (url.indexOf("#") !== -1) {
        const splittedUrl = url.split("#");
        // if url only has hash values, insert k-v pair before the '#'
        url = splittedUrl[0] + `?${QUERY_PARAM_KEY}=${Date.now()}#` + splittedUrl[1]
    } else {
        // if no params or hash values are found, append k-v pair to the end
        return (url + `?${QUERY_PARAM_KEY}=${Date.now()}`)
    }
    return url
}

export const queryGeolocationByHttpGetRequest =
    (hasConsoleNavFooter = false, url: string = DEFAULT_GEOLOCATION_URL, timeoutMillis = 5000, defaultLogger: Log = DEFAULT_LOGGER): GeolocationQuery => {
        function logLatency(log: Log, latency: number, region: string, message: string, status: number) {
            log("info")(METRICS.CHECK_GEOLOCATION_SUCCESS, {
                metric: latency,
                region,
                detail: message,
                url,
                status: status
            });
        }

        return (geolocatedIn: GeolocationQueryResultCallback, log: Log = defaultLogger || DEFAULT_LOGGER) => {
            // Always suppress the banner for domains that end with ".cn"
            if (window.location.hostname.endsWith('.cn')) {
                return geolocatedIn("NON-EU")
            }

            if (isESCPartition()) {
                return geolocatedIn("EU")
            }
            const geoServiceStart = Date.now();

            const makeRequest = (requestUrl: string, isFallback: boolean = false) => {
                const xhr = new XMLHttpRequest();
                let region: GeolocationRegion = "EU"; // default assumption
                let geoRespStatus = 200; // default assumption is 200 (in-EU)
                if (hasConsoleNavFooter) xhr.overrideMimeType("application/json");

                xhr.addEventListener("load", function (this: XMLHttpRequest) {
                    geoRespStatus = this.status;
                    if (!hasConsoleNavFooter || geoRespStatus === 304) {
                        // non-console integration or got 304
                        region = geoRespStatus === 403 ? "NON-EU" : "EU";
                    } else {
                        // handle response body returned from DEFAULT_CONSOLE_INTEGRATION_GEOLOCATION_URL
                        try {
                            let geoRespBody = JSON.parse(this.response);
                            geoRespStatus = geoRespBody.status;
                            if (geoRespStatus === 403) region = "NON-EU";
                        } catch (e) {
                            log("error")("geolocationResponseError", { url: requestUrl, detail: "Failed to Parse the Received Geolocation Response" });
                        }
                    }
                    logLatency(log, Date.now() - geoServiceStart, region, `Geolocation Response Received${isFallback ? ' (Fallback)' : ''}`, geoRespStatus);
                    geolocatedIn(region);
                });

                const timeoutHandler = () => {
                    if (!isFallback) { // First request's fallback is 'false' and timed out
                        // Try fallback domain
                        makeRequest(FALLBACK_CONSOLE_INTEGRATION_GEOLOCATION_URL, true);
                    } else {
                        // Both attempts failed, default to EU
                        const region = "EU";
                        const message = "Geolocation Request Failed (Both Domains)";
                        logLatency(log, timeoutMillis, region, message, 0);
                        log("error")("geolocationRequestError", {
                            url,
                            fallbackUrl: requestUrl,
                            timeoutSetting: timeoutMillis,
                            detail: message
                        });
                        geolocatedIn(region);
                    }
                };

                xhr.addEventListener("timeout", timeoutHandler);
                xhr.addEventListener("error", timeoutHandler);
                xhr.open("GET", timestampUrl(requestUrl));
                xhr.timeout = timeoutMillis;
                xhr.send();
            };
            makeRequest(url);
        };
    }

export const COUNTRY_POLICIES_URL = "https://api.prod.tools.shortbread.aws.dev";
export const COUNTRY_POLICIES_SESSION_STORAGE_KEY = "awsmcc-c";
const COUNTRY_POLICIES_TIMEOUT_MILLIS = 1500;

export const DEFAULT_COUNTRY_POLICIES: CountryPolicies = {
    isShowCookieBannerCountry: true,
    showCcbaOption: true
}
export const CN_COUNTRY_POLICIES: CountryPolicies = {
    isShowCookieBannerCountry: false,
    showCcbaOption: false
}
export const ESC_COUNTRY_POLICIES: CountryPolicies = {
    isShowCookieBannerCountry: true,
    showCcbaOption: false
}

const isCountryPoliciesObject = (value: unknown): value is CountryPolicies => {
    return (
        typeof value === "object" &&
        value !== null &&
        typeof (value as CountryPolicies).isShowCookieBannerCountry === "boolean" &&
        typeof (value as CountryPolicies).showCcbaOption === "boolean"
    );
};

/**
 * Reads and validates cached CountryPolicies from session storage.
 * Returns null if the key is absent, the value cannot be parsed, or it does
 * not serialize to the CountryPolicies type.
 */
const readCountryPoliciesFromSessionStorage = (): CountryPolicies | null => {
    try {
        const raw = window.sessionStorage.getItem(COUNTRY_POLICIES_SESSION_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return isCountryPoliciesObject(parsed) ? parsed : null;
    } catch (e) {
        return null;
    }
};

const saveCountryPoliciesToSessionStorage = (policies: CountryPolicies): void => {
    try {
        window.sessionStorage.setItem(COUNTRY_POLICIES_SESSION_STORAGE_KEY, JSON.stringify(policies));
    } catch (e) {
        // Session storage may be unavailable (e.g. disabled/full); ignore.
    }
};

export interface QueryUserCountryPoliciesOptions {
    url?: string;
    timeoutMillis?: number;
    defaultLogger?: Log;
}

/**
 * Resolves the user's country policies.
 *
 * 1. Returns the cached CountryPolicies from session storage if present and valid.
 * 2. Otherwise issues a GET request to the policies endpoint.
 *    - On a 200 response with a valid CountryPolicies body, caches it in session
 *      storage and resolves with it.
 *    - On error, resolves with null.
 *    - If the request takes longer than `timeoutMillis`, resolves with null but
 *      leaves the request in flight so that a late 200 response is still cached
 *      in session storage for subsequent reads.
 */
export const queryUserCountryPoliciesByApiGetRequest = ({
    url = COUNTRY_POLICIES_URL,
    timeoutMillis = COUNTRY_POLICIES_TIMEOUT_MILLIS,
    defaultLogger = DEFAULT_LOGGER
}: QueryUserCountryPoliciesOptions = {}): CountryPoliciesQuery => {
    return (onResult: CountryPoliciesQueryResultCallback, hasCacheConsent: boolean, log: Log = defaultLogger || DEFAULT_LOGGER) => {
        const requestStart = Date.now();
        
        // Always suppress the banner for domains that end with ".cn"
        if (window.location.hostname.endsWith('.cn')) {
            return onResult(CN_COUNTRY_POLICIES);
        }

        if (isESCPartition()) {
            return onResult(ESC_COUNTRY_POLICIES);
        }
        
        if (hasCacheConsent) {
            const cached = readCountryPoliciesFromSessionStorage();
            if (cached) {
                return onResult(cached);
            }
        }

        const logLatency = (latency: number, message: string, status: number) => {
            log("info")(METRICS.CHECK_COUNTRY_POLICIES_LATENCY, {
                latency,
                detail: message,
                url,
                status,
            });
        };

        // Ensure onResult is only delivered once, while still allowing a late
        // response to populate session storage after a timeout.
        let settled = false;
        const settle = (result?: CountryPolicies) => {
            if (settled) return;
            settled = true;
            onResult(result || DEFAULT_COUNTRY_POLICIES);
        };

        // Resolve with defaults but keep the request alive so a late
        // successful 200 response can still be cached for later reads.
        const deadlineTimeout = setTimeout(() => {
            const message = "Country Policies Request Timed Out. Default country policies will be applied.";
            log("error")("countryPoliciesRequestTimeout", {
                url,
                timeoutSetting: timeoutMillis,
                detail: message,
            });
            settle();
        }, timeoutMillis);

        const xhr = new XMLHttpRequest();

        xhr.addEventListener("load", function (this: XMLHttpRequest) {
            clearTimeout(deadlineTimeout);
            const latency = Date.now() - requestStart;
            if (this.status === 200) {
                try {
                    const parsed = JSON.parse(this.response);
                    if (isCountryPoliciesObject(parsed)) {
                        if (hasCacheConsent) {
                            saveCountryPoliciesToSessionStorage(parsed);
                        }
                        logLatency(latency, "Country Policies Response Received", this.status);
                        settle(parsed);
                        return;
                    }
                    log("error")("countryPoliciesResponseError", {
                        url,
                        detail: "Received Country Policies Response did not match expected type",
                    });
                } catch (e) {
                    log("error")("countryPoliciesResponseError", {
                        url,
                        detail: "Failed to Parse the Received Country Policies Response",
                    });
                }
            } else {
                log("error")("countryPoliciesResponseError", {
                    url,
                    status: this.status,
                    detail: "Country Policies Response returned a non-200 status",
                });
            }
            logLatency(latency, "Country Policies Request Failed", this.status);
            settle();
        });

        const failHandler = () => {
            clearTimeout(deadlineTimeout);
            const message = "Country Policies Request Failed. Default country policies will be applied.";
            logLatency(Date.now() - requestStart, message, 0);
            log("error")("countryPoliciesRequestError", {
                url,
                detail: message,
            });
            settle();
        };

        xhr.addEventListener("error", failHandler);
        xhr.addEventListener("abort", failHandler);
        log("info")(METRICS.CHECK_COUNTRY_POLICIES_ATTEMPT, { url });

        xhr.open("GET", url);
        xhr.send();
    };
};

export type CountryPolicies = {
    // This boolean is currently unused, it will be used to replace the queryGeolocationByHttpGetRequest
    // function above as a cookie banner signal in a future implementation
    isShowCookieBannerCountry: boolean, 
    showCcbaOption: boolean,
}

export type CountryPoliciesQueryResultCallback = (policies: CountryPolicies | null) => void;
export type CountryPoliciesQuery = (onResult: CountryPoliciesQueryResultCallback, hasCacheConsent: boolean, log?: Log) => void;

export type GeolocationRegion = "EU" | "NON-EU";
export type GeolocationQueryResultCallback = (region: GeolocationRegion) => void;
export type GeolocationQuery = (geolocatedIn: GeolocationQueryResultCallback, log?: Log) => void;
export default queryGeolocationByHttpGetRequest;
